import * as React from "react";
import * as React44ms from "../../../Util/form-utils/react-44ms";

import { SingleLineTextInput2023ViewModel } from "../ui2023";

export enum FormLogicEventBehaviour {
  nothing = "nothing",
  resetValidation = "resetValidation",
  validate = "validate",
}

export enum FormLogicEventBehaviour_OnBlur {
  validateAsAnEffect = "validateAsAnEffect",
}

export enum FormLogicEventBehaviour_OnChange {
  nothing = "nothing",
  onlyMutateWithoutValidation_ButKeepTheLastValidation = "onlyMutateWithoutValidation_ButKeepTheLastValidation",
  onlyMutateWithoutValidation_AndResetValidationOnMutation = "onlyMutateWithoutValidation_AndResetValidationOnMutation",
  mutateWithValidation = "mutateWithValidation",
}

type OnlyTheEventHandlers<
  YourJSXIntrinsicElementKey extends keyof JSX.IntrinsicElements
> = {
  [AnyEventHandler in keyof JSX.IntrinsicElements[YourJSXIntrinsicElementKey]]: JSX.IntrinsicElements[YourJSXIntrinsicElementKey][AnyEventHandler];
};
type AnyInputEventHandlerKey = OnlyTheEventHandlers<"input">;
type AnyInputEventHandlerKey_ExceptPercular = Omit<
  AnyInputEventHandlerKey,
  "onChange" | "onBlur"
>;
type GenericTextFieldFormLogicBehaviour_ImmutableEvents = {
  [AnyEventHandler in keyof AnyInputEventHandlerKey_ExceptPercular]?: FormLogicEventBehaviour;
};
type GenericTextFieldFormLogicBehaviour_ImmutableEvents_OnBlur = {
  onBlur?: FormLogicEventBehaviour | FormLogicEventBehaviour_OnBlur;
};
interface GenericTextFieldFormLogicBehaviour_MutableEvents {
  onChange?: FormLogicEventBehaviour_OnChange;
}

export type GenericTextFieldFormLogicBehaviour =
  GenericTextFieldFormLogicBehaviour_ImmutableEvents &
    GenericTextFieldFormLogicBehaviour_ImmutableEvents_OnBlur &
    GenericTextFieldFormLogicBehaviour_MutableEvents;

export interface GenericTextFieldFormLogicProps
  extends UseGenericTextFieldFormLogicPureProps {
  ref: React44ms.Api.TypedRef<SingleLineTextInput2023ViewModel>;

  behaviour: GenericTextFieldFormLogicBehaviour;
}
export const GenericTextFieldFormLogicPure: React44ms.Api.FunctionComponent<
  GenericTextFieldFormLogicProps
> = (props) => {
  const [
    state,
    handleAnythingElse,
    handleChange,
    all_good_to_submit,
    { setValue, ...allTheOtherSetters },
  ] = useGenericTextFieldFormLogic(
    {
      ...props,
    },
    ""
  );

  const [needsAValidationInEffect, setNeedsAValidationInEffect] =
    React44ms.Api.useState(false);
  const handleValidateAsAnEffect = React44ms.Api.useCallback(() => {
    setNeedsAValidationInEffect(true);
  }, []);
  React44ms.Api.useEffect(() => {
    if (needsAValidationInEffect) {
      handleAnythingElse();
      setNeedsAValidationInEffect(false);
    }
  }, [needsAValidationInEffect]);

  const justDoNothing = React44ms.Api.useCallback(() => {
    // do nothing )the long version(:
    const doing_nothing__with_style = 3.14 + 11;
    const absolutely_nothing = doing_nothing__with_style + 11;
    // @ts-ignore
    if (absolutely_nothing === "the best") {
      //
    }
    return;
  }, []);
  const justResetValidation = React44ms.Api.useCallback(() => {
    Object.keys(allTheOtherSetters).forEach((key) => {
      (allTheOtherSetters as { [key: string]: (newValue: any) => void })[key](
        undefined
      );
    });
  }, []);
  const justChange: React44ms.Api.input["onChange"] = React44ms.Api.useCallback(
    (event) => {
      setValue(event.target.value);
    },
    []
  );
  const justChangeAndReset: React44ms.Api.input["onChange"] =
    React44ms.Api.useCallback((event) => {
      setValue(event.target.value);
      justResetValidation();
    }, []);

  const handleSubmit: React44ms.Api.form["onSubmit"] =
    React44ms.Api.useCallback(() => {
      React44ms.Api.submit(
        props.behaviour["onSubmit"] === FormLogicEventBehaviour.validate
          ? (all_good_to_submit() as unknown as true)
          : true
      );
    }, [props.behaviour["onSubmit"], all_good_to_submit]);

  console.log(`RENDERING FORM LOGIC -> [${props.ref}]`);

  return (
    <form React44ms_onSubmit={handleSubmit}>
      <input
        onFocus={
          props.behaviour["onFocus"] === FormLogicEventBehaviour.validate
            ? handleAnythingElse
            : props.behaviour["onFocus"] ===
              FormLogicEventBehaviour.resetValidation
            ? justResetValidation
            : justDoNothing
        }
        React44ms_onChange={
          props.behaviour["onChange"] ===
          FormLogicEventBehaviour_OnChange.mutateWithValidation
            ? handleChange
            : props.behaviour["onChange"] ===
              FormLogicEventBehaviour_OnChange.onlyMutateWithoutValidation_AndResetValidationOnMutation
            ? justChangeAndReset
            : props.behaviour["onChange"] ===
              FormLogicEventBehaviour_OnChange.onlyMutateWithoutValidation_ButKeepTheLastValidation
            ? justChange
            : justDoNothing
        }
        onBlur={
          props.behaviour["onBlur"] ===
          FormLogicEventBehaviour_OnBlur.validateAsAnEffect
            ? handleValidateAsAnEffect
            : props.behaviour["onBlur"] === FormLogicEventBehaviour.validate
            ? handleAnythingElse
            : props.behaviour["onBlur"] ===
              FormLogicEventBehaviour.resetValidation
            ? justResetValidation
            : justDoNothing
        }
      />
      <output React44ms_value={state} />
    </form>
  );
};
export const GenericTextFieldFormLogic = React44ms.React.Reactify(
  GenericTextFieldFormLogicPure
);

interface UseGenericTextFieldFormLogicPureProps {
  required?: boolean;
  requirementMessage?: string;
  trim?: boolean;
  minLength?: number | undefined;
  maxLength?: number | undefined;
  conformsToRegex?: RegExp;
  conformsToRegexMessage?: string;
  customLength?: (
    value: string | null | undefined,
    props: UseGenericTextFieldFormLogicPureProps
  ) => { value: any };
  customWarningValidation?: (
    value: string | null | undefined,
    props: UseGenericTextFieldFormLogicPureProps
  ) => string | null | undefined | false;
  customErrorValidation?: (
    value: string | null | undefined,
    props: UseGenericTextFieldFormLogicPureProps
  ) => string | null | undefined | false;
  noticeGenerator?: (
    value: string | null | undefined,
    props: UseGenericTextFieldFormLogicPureProps
  ) => any | undefined;
  valueMustBeOneOf?: (props: UseGenericTextFieldFormLogicPureProps) => any[];
  disabled?: boolean;
}
export type UseGenericTextFieldFormLogicProps =
  Required<UseGenericTextFieldFormLogicPureProps>;

const useGenericTextFieldFormLogic = (
  props: UseGenericTextFieldFormLogicPureProps,
  initialState: string | null | undefined
) => {
  const [value, setValue] = React44ms.Api.useState<string | null | undefined>(
    initialState
  );
  const [error, setError] = React44ms.Api.useState<string | undefined>(
    undefined
  );
  const [warning, setWarning] = React44ms.Api.useState<string | undefined>(
    undefined
  );
  const [notice, setNotice] = React44ms.Api.useState<any>(undefined);

  const deps = [
    props.trim,
    props.customLength,
    props.maxLength,
    props.valueMustBeOneOf,
    props.minLength,
    props.required,
    props.requirementMessage,
    props.customWarningValidation,
    props.customErrorValidation,
    props.noticeGenerator,
    props.conformsToRegex,
    props.conformsToRegexMessage,
  ];

  const handleAnythingElse = React44ms.Api.useCallback(() => {
    const [newValue, errorIfAny, warningIfAny, noticeIfAny] = generalPurpose(
      props,
      value,
      value
    );
    if (newValue !== value) {
      setValue(newValue);
    }
    if (errorIfAny !== error) {
      setError(errorIfAny);
    }
    if (warningIfAny !== warning) {
      setWarning(warningIfAny);
    }
    if (noticeIfAny !== notice) {
      setNotice(noticeIfAny);
    }
  }, [...deps, value, error, warning, notice]);

  const handleChange: React44ms.Api.input["onChange"] =
    React44ms.Api.useCallback(
      (event) => {
        const [newValue, errorIfAny, warningIfAny, noticeIfAny] =
          generalPurpose(props, value, event.target.value);
        if (newValue !== value) {
          setValue(newValue);
        }
        if (errorIfAny !== error) {
          setError(errorIfAny);
        }
        if (warningIfAny !== warning) {
          setWarning(warningIfAny);
        }
        if (noticeIfAny !== notice) {
          setNotice(noticeIfAny);
        }
      },
      [...deps, value, error, warning, notice]
    );

  const handleSubmit: () => true | false = React44ms.Api.useCallback(() => {
    let hasAnythingChanged = false;
    const [newValue, errorIfAny, warningIfAny, noticeIfAny] = generalPurpose(
      props,
      value,
      value
    );
    if (newValue !== value) {
      hasAnythingChanged = true;
      setValue(newValue);
    }
    if (errorIfAny !== error) {
      hasAnythingChanged = true;
      setError(errorIfAny);
    }
    if (warningIfAny !== warning) {
      hasAnythingChanged = true;
      setWarning(warningIfAny);
    }
    if (noticeIfAny !== notice) {
      hasAnythingChanged = true;
      setNotice(noticeIfAny);
    }
    return !hasAnythingChanged && !errorIfAny;
  }, [...deps, value, error, warning, notice]);

  const publicValue: SingleLineTextInput2023ViewModel = React44ms.Api.useMemo(
    () => ({
      value: value || "",
      error,
      warning,
      notice,
      required: !!props.required,
      disabled: !!props.disabled,
    }),
    [value, error, warning, notice, props.required, props.disabled]
  );

  const output: [
    SingleLineTextInput2023ViewModel,
    () => void,
    React44ms.Api.input["onChange"],
    () => true | false,
    {
      setValue: React44ms.Api.UseStateSetter<string | null | undefined>;
      setError: React44ms.Api.UseStateSetter<string | undefined>;
      setWarning: React44ms.Api.UseStateSetter<string | undefined>;
      setNotice: React44ms.Api.UseStateSetter<any>;
    }
  ] = React44ms.Api.useMemo(
    () => [
      publicValue,
      handleAnythingElse,
      handleChange,
      handleSubmit,
      {
        setValue,
        setError,
        setWarning,
        setNotice,
      },
    ],
    [publicValue, handleAnythingElse, handleChange, handleSubmit]
  );

  return output;
};

type Error = string | undefined;
type Warning = string | undefined;
type Notice = any | undefined;
const generalPurpose = (
  props: UseGenericTextFieldFormLogicPureProps,
  previousValue: string | null | undefined,
  newValue_original: string | null | undefined
): [string | null | undefined, Error, Warning, Notice] => {
  let newValue = newValue_original;
  if (props.trim) {
    newValue = newValue?.trim();
  }

  if (props.customLength) {
    const { value: customLengthAdjustedValue } = props.customLength(
      newValue,
      props
    );
    newValue = customLengthAdjustedValue;
  }
  if (props.maxLength) {
    newValue = newValue?.substring(0, props.maxLength);
  }

  if (props.valueMustBeOneOf) {
    const allowed = props.valueMustBeOneOf(props);
    if (!allowed.includes(newValue)) {
      newValue = previousValue;
    }
  }

  let firstError;
  let firstWarning;
  let noticeIsOutdated = false;
  /**
   * Minimum length
   */
  if (!noticeIsOutdated) {
    if (props.minLength) {
      if ((newValue?.length || 0) < props.minLength) {
        noticeIsOutdated = true;
        firstError = `Minimum length is ${props.minLength} symbols.`;
      }
    }
  }
  /**
   * Required
   */
  if (!noticeIsOutdated) {
    const isEmpty = !newValue || newValue?.length < 1;
    if (props.required && isEmpty) {
      noticeIsOutdated = true;
      firstError = props.requirementMessage || "This field is required.";
    }
  }
  /**
   * Custom warning validation
   */
  if (!noticeIsOutdated) {
    let validityWarning = null;
    if (props.customWarningValidation) {
      validityWarning = props.customWarningValidation(newValue, props);
    }
    if (validityWarning) {
      noticeIsOutdated = true;
      firstWarning = validityWarning;
    }
  }
  /**
   * Custom error validation
   */
  if (!noticeIsOutdated) {
    let validityError = null;
    if (props.customErrorValidation) {
      validityError = props.customErrorValidation(newValue, props);
    }
    if (validityError) {
      noticeIsOutdated = true;
      firstError = validityError;
    }
  }
  /**
   * Regex conforms
   */
  if (!noticeIsOutdated) {
    let validityError = null;
    if (props.conformsToRegex) {
      const doesConform = (newValue || "").match(props.conformsToRegex);
      if (!doesConform) {
        validityError = props.conformsToRegexMessage;
      }
    }
    if (validityError) {
      noticeIsOutdated = true;
      firstError = validityError;
    }
  }

  let theRealNextValue = previousValue;
  const itsDifferentFromWhatWouldBeShownWithoutChangingIt =
    newValue !== previousValue || newValue != newValue_original;
  if (itsDifferentFromWhatWouldBeShownWithoutChangingIt) {
    noticeIsOutdated = true;
    theRealNextValue = newValue;
  }

  let notice;
  if (noticeIsOutdated) {
    const freshlyGeneratedNotice = props.noticeGenerator?.(newValue, props);
    if (freshlyGeneratedNotice) {
      notice = freshlyGeneratedNotice;
    }
  }

  return [theRealNextValue, firstError, firstWarning, notice];
};
